import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  approveAvailability,
  getPendingAvailability,
  getPendingAvailabilityCount,
  rejectAvailability,
} from '../services/availabilityApprovalService';
import type {
  AvailabilityApprovalFiltersState,
  AvailabilityApprovalQuery,
  AvailabilityApprovalRow,
  AvailabilityApprovalSortColumn,
  AvailabilityApprovalSortDirection,
} from '../types/admin';

export const defaultAvailabilityApprovalFilters: AvailabilityApprovalFiltersState = {
  approvalStatus: '',
  submittedFrom: '',
  submittedTo: '',
  hoursPerWeek: '',
  availabilityType: '',
};

const toIsoDate = (date: Date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Until the approver picks a date filter, keep the list scoped to the
// current calendar month so old/decided requests don't pile up on load.
const getCurrentMonthBounds = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { start: toIsoDate(start), end: toIsoDate(end) };
};

const getErrorMessage = (error: any) => {
  const code = error?.code ? `${error.code}: ` : '';
  return `${code}${error?.message || 'Unable to process availability request.'}`;
};

export const useAvailabilityApprovals = () => {
  const [rows, setRows] = useState<AvailabilityApprovalRow[]>([]);
  const [recordsTotal, setRecordsTotal] = useState(0);
  const [recordsFiltered, setRecordsFiltered] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortColumn, setSortColumn] = useState<AvailabilityApprovalSortColumn>('SubmittedDate');
  const [sortDirection, setSortDirection] = useState<AvailabilityApprovalSortDirection>('Descending');
  const [filters, setFilters] = useState<AvailabilityApprovalFiltersState>(defaultAvailabilityApprovalFilters);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [actionLoadingKey, setActionLoadingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPageNumber(1);
    }, 500);

    return () => window.clearTimeout(timer);
  }, [search]);

  const query: AvailabilityApprovalQuery = useMemo(() => {
    let effectiveFilters = filters;

    if (!filters.submittedFrom && !filters.submittedTo) {
      const { start, end } = getCurrentMonthBounds();
      effectiveFilters = { ...filters, submittedFrom: start, submittedTo: end };
    }

    return {
      pageNumber,
      pageSize,
      search: debouncedSearch,
      sortColumn,
      sortDirection,
      filters: effectiveFilters,
    };
  }, [debouncedSearch, filters, pageNumber, pageSize, sortColumn, sortDirection]);

  const refresh = useCallback(async (nextQuery = query) => {
    setError(null);
    setIsFetching(true);
    if (!hasLoadedRef.current) setLoading(true);

    try {
      const result = await getPendingAvailability(nextQuery);
      setRows(result.data);
      setRecordsTotal(result.recordsTotal);
      setRecordsFiltered(result.recordsFiltered);
      const count = await getPendingAvailabilityCount();
      setPendingCount(count);
      return result;
    } catch (err: any) {
      const message = getErrorMessage(err);
      setError(message);
      throw err;
    } finally {
      setLoading(false);
      setIsFetching(false);
      hasLoadedRef.current = true;
    }
  }, [query]);

  useEffect(() => {
    refresh().catch(() => {});
  }, [refresh]);

  const applyFilters = useCallback((nextFilters: AvailabilityApprovalFiltersState) => {
    setFilters(nextFilters);
    setPageNumber(1);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(defaultAvailabilityApprovalFilters);
    setPageNumber(1);
  }, []);

  const changeSort = useCallback((column: AvailabilityApprovalSortColumn) => {
    setPageNumber(1);
    setSortColumn((currentColumn) => {
      if (currentColumn !== column) {
        setSortDirection('Ascending');
        return column;
      }

      setSortDirection((currentDirection) =>
        currentDirection === 'Ascending' ? 'Descending' : 'Ascending'
      );
      return currentColumn;
    });
  }, []);

  const changePageSize = useCallback((nextPageSize: number) => {
    setPageSize(nextPageSize);
    setPageNumber(1);
  }, []);

  const approve = useCallback(async (row: AvailabilityApprovalRow) => {
    const key = `${row.userId}-${row.submittedDate}`;
    setActionLoadingKey(key);
    setError(null);

    try {
      await approveAvailability({
        userId: row.userId,
        submittedDate: row.submittedDate,
      });
      await refresh();
    } catch (err: any) {
      const message = getErrorMessage(err);
      setError(message);
      throw err;
    } finally {
      setActionLoadingKey(null);
    }
  }, [refresh]);

  const reject = useCallback(async (row: AvailabilityApprovalRow, rejectionReason: string) => {
    const key = `${row.userId}-${row.submittedDate}`;
    setActionLoadingKey(key);
    setError(null);

    try {
      await rejectAvailability({
        userId: row.userId,
        submittedDate: row.submittedDate,
        rejectionReason,
      });
      await refresh();
    } catch (err: any) {
      const message = getErrorMessage(err);
      setError(message);
      throw err;
    } finally {
      setActionLoadingKey(null);
    }
  }, [refresh]);

  return {
    rows,
    recordsTotal,
    recordsFiltered,
    pendingCount,
    pageNumber,
    pageSize,
    search,
    sortColumn,
    sortDirection,
    filters,
    loading,
    isFetching,
    actionLoadingKey,
    error,
    setSearch,
    setPageNumber,
    changePageSize,
    changeSort,
    applyFilters,
    resetFilters,
    refresh,
    approve,
    reject,
  };
};
